import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import BrandNav from "../components/BrandNav";
import SubmitHomework from "../components/SubmitHomework";
import CelebrationModal from "../components/CelebrationModal";
import { api, audioUrl } from "../lib/api";
import { extractError } from "../lib/format";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

export default function Lesson() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [newCerts, setNewCerts] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setErr("");
    setLesson(null);
    api
      .get(`/lessons/${encodeURIComponent(lessonId)}`)
      .then(async ({ data }) => {
        if (!alive) return;
        setLesson(data);
        // fetch siblings for prev/next
        const { data: list } = await api.get(`/catalog/${data.age_band}/${data.term}/lessons`);
        if (alive) setSiblings(list.lessons || []);
        // check progress
        const { data: enr } = await api.get("/enrollments/me");
        const mine = (enr.enrollments || []).find((e) => e.age_band === data.age_band);
        if (mine?.lesson_progress?.[data.id]?.completed_at) setDone(true);
      })
      .catch((e) => {
        if (alive) setErr(extractError(e, "Could not load lesson."));
      });
    return () => {
      alive = false;
    };
  }, [lessonId]);

  const { prev, next } = useMemo(() => {
    if (!lesson || !siblings.length) return { prev: null, next: null };
    const idx = siblings.findIndex((s) => s.id === lesson.id);
    return {
      prev: idx > 0 ? siblings[idx - 1] : null,
      next: idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null,
    };
  }, [lesson, siblings]);

  const markComplete = async () => {
    if (!lesson || done) return;
    setSaving(true);
    try {
      const played = audioRef.current?.currentTime || 0;
      const { data } = await api.post(`/enrollments/lessons/${encodeURIComponent(lesson.id)}/complete`, {
        played_seconds: played,
      });
      setDone(true);
      toast.success("Marked complete. Well done.");
      if (Array.isArray(data?.new_certificates) && data.new_certificates.length) {
        setNewCerts(data.new_certificates);
      }
    } catch (e) {
      toast.error(extractError(e, "Could not save progress."));
    } finally {
      setSaving(false);
    }
  };

  if (err) {
    return (
      <div>
        <BrandNav />
        <main className="ftg-shell ftg-page">
          <div className="ftg-card" data-testid="lesson-error-card">
            <h2 style={{ marginTop: 0 }}>{"This lesson isn't available."}</h2>
            <p style={{ color: "var(--ftg-ink-mute)" }}>{err}</p>
            <Link to="/dashboard" className="ftg-btn ftg-btn--primary" data-testid="lesson-back-dashboard">
              Back to dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div>
        <BrandNav />
        <div className="ftg-loader" data-testid="lesson-loader">
          <div className="ftg-loader__dot" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="lesson-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <div className="ftg-lesson">
          <div className="ftg-lesson__head">
            <div>
              <div className="ftg-lesson__meta" data-testid="lesson-meta">
                Term {lesson.term} · Week {String(lesson.week).padStart(2, "0")} · Lesson {lesson.lesson_number}
              </div>
              <h1 className="ftg-lesson__title" data-testid="lesson-title">{lesson.title}</h1>
            </div>
            <Link to="/dashboard" className="ftg-btn ftg-btn--ghost" data-testid="back-to-dashboard-btn">
              <ArrowLeft size={16} /> Dashboard
            </Link>
          </div>

          <div className="ftg-lesson__player" data-testid="lesson-player">
            {lesson.has_audio ? (
              <audio
                ref={audioRef}
                controls
                preload="metadata"
                src={audioUrl(lesson.id)}
                data-testid="lesson-audio"
              />
            ) : (
              <p style={{ color: "var(--ftg-ink-mute)" }} data-testid="lesson-no-audio">
                Audio is not available for this lesson.
              </p>
            )}
          </div>

          <div className="ftg-lesson__body">
            <div className="ftg-md" data-testid="lesson-markdown">
              <ReactMarkdown>{lesson.markdown}</ReactMarkdown>
            </div>
          </div>

          <SubmitHomework lessonId={lesson.id} />

          <div className="ftg-lesson__nav">
            {prev ? (
              <Link
                to={`/lesson/${encodeURIComponent(prev.id)}`}
                className="ftg-btn ftg-btn--ghost"
                data-testid="prev-lesson-btn"
              >
                <ArrowLeft size={16} /> Previous
              </Link>
            ) : (
              <span />
            )}
            <button
              onClick={markComplete}
              disabled={saving || done}
              className={`ftg-btn ${done ? "ftg-btn--ghost" : "ftg-btn--secondary"}`}
              data-testid="mark-complete-btn"
            >
              {done ? (
                <>
                  <CheckCircle2 size={16} /> Completed
                </>
              ) : saving ? (
                "Saving…"
              ) : (
                <>
                  <CheckCircle2 size={16} /> Mark complete
                </>
              )}
            </button>
            {next ? (
              <Link
                to={`/lesson/${encodeURIComponent(next.id)}`}
                className="ftg-btn ftg-btn--primary"
                data-testid="next-lesson-btn"
              >
                Next <ArrowRight size={16} />
              </Link>
            ) : (
              <span />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
